// tienda/PreciosSection.jsx

import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { brand, fadeUp } from "../shared/brand";

const DELIVERY_SERVICES = [
  {
    label: "Clásico",
    price: "S/ 9.90",
    sub: "Entrega estándar para tiendas que ya coordinan bien sus pedidos.",
    popular: false,
    color: brand.primary,
    tag: "Base",
    perks: [
      "Recojo en tienda registrado hasta 12:00 PM",
      "Entrega same-day si el pedido se registra hasta 2:00 PM",
      "Mensajería automática y atención operativa al cliente final",
      "Un intento de entrega incluido",
      "COD disponible cuando aplique",
    ],
  },
  {
    label: "Coordinado",
    price: "S/ 14.90",
    sub: "La mejor opción para ecommerce que quiere reducir rechazos y no respuestas.",
    popular: true,
    color: brand.accentGold,
    tag: "Mejor para ecommerce",
    perks: [
      "Un reintento incluido en distritos elegibles",
      "COD completo: efectivo y medios de pago habilitados",
      "Manejo de efectivo y conciliación visible para tu tienda",
      "Inventario, almacén activo, packing y fulfillment si el producto está en Fast Max",
      "Tracking reforzado, ubicación del driver cuando esté disponible e interfaz para cliente final",
    ],
  },
  {
    label: "Premium",
    price: "S/ 19.90",
    sub: "Prioridad de ruta para pedidos urgentes o de alto valor.",
    popular: false,
    color: brand.plum,
    tag: "Prioridad",
    perks: [
      "Incluye beneficios de Coordinado, sujeto a elegibilidad operativa",
      "Se procura despachar entre las primeras entregas posibles de la ruta",
      "Entrega lo más rápido posible dentro de la operación del día",
      "Hasta 10 minutos adicionales de espera razonable en destino",
      "Mayor seguimiento operativo para incidencias críticas",
    ],
  },
];

const ADDONS = [
  {
    label: "Reintento preventivo",
    price: "+S/ 3.00",
    sub: "Útil para pedidos Clásico cuando quieres más protección antes de registrar la entrega.",
    color: brand.accentGreen,
  },
  {
    label: "Reintento posterior a falla",
    price: "desde S/ 8.00",
    sub: "Si una entrega falla sin reintento elegible, puedes solicitar un nuevo intento según disponibilidad.",
    color: brand.primary,
  },
  {
    label: "Más add-ons",
    price: "pronto",
    sub: "Seguiremos agregando opciones para empaques, prioridad, coordinación y automatización operativa.",
    color: brand.plum,
  },
];

const COMPARISON_ROWS = [
  ["Precio base", "S/ 9.90", "S/ 14.90", "S/ 19.90"],
  ["Intentos incluidos", "1", "1 + reintento elegible", "1 + reintento elegible"],
  ["COD y medios de pago", "Disponible", "Completo", "Completo"],
  ["Inventario Fast Max", "Según operación", "Incluido si el producto está en FM", "Incluido si el producto está en FM"],
  ["Packing / fulfillment", "Cuando aplique", "Sí, desde almacén FM", "Sí, desde almacén FM"],
  ["Tracking para cliente final", "Básico", "Reforzado", "Reforzado + prioridad"],
  ["Prioridad de ruta", "No", "Normal", "Alta"],
  ["Espera en destino", "Estándar", "Estándar", "+10 min razonables"],
];

const SUBSCRIPTION_PLANS = [
  {
    label: "Plan Plus",
    price: "S/ 49",
    period: "/mes",
    sub: "Para tiendas que empiezan a operar con más control.",
    popular: false,
    perks: [
      "Soporte prioritario",
      "Reportes básicos de entregas y contraentrega",
      "Descuentos en add-ons como reintentos elegibles",
      "Mayor visibilidad operativa para tomar decisiones",
    ],
  },
  {
    label: "Plan Pro",
    price: "S/ 149",
    period: "/mes",
    sub: "Para tiendas que quieren escalar operación, inventario y visibilidad.",
    popular: true,
    perks: [
      "Prioridad dentro de la operación Fast Max",
      "Reportes avanzados de entregas y COD",
      "Herramientas de inventario y fulfillment",
      "Preparado para carga masiva/API más adelante",
    ],
  },
];

export function PreciosSection() {
  return (
    <section id="precios" className="border-b scroll-mt-16" style={{ background: brand.bg, borderColor: brand.line }}>
      <div className="mx-auto max-w-7xl px-4 py-14 md:py-20">
        <motion.div {...fadeUp} className="mx-auto mb-10 max-w-2xl text-center">
          <p className="mb-3 text-xs font-black uppercase tracking-[0.2em]" style={{ color: brand.primary }}>
            Tarifas por nivel de calidad
          </p>
          <h2 className="text-3xl font-black md:text-4xl" style={{ color: brand.ink }}>
            No todos los pedidos necesitan el mismo nivel de coordinación.
          </h2>
          <p className="mt-4 text-sm leading-relaxed" style={{ color: brand.muted }}>
            Clásico entrega. Coordinado aumenta la probabilidad de entrega efectiva para ecommerce.
            Premium prioriza pedidos urgentes o sensibles dentro de la ruta.
          </p>
        </motion.div>

        <div className="mx-auto grid max-w-6xl gap-4 md:grid-cols-3">
          {DELIVERY_SERVICES.map((plan, index) => (
            <motion.div
              key={plan.label}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: index * 0.06 }}
              className="relative overflow-hidden rounded-2xl border bg-white p-6 text-center shadow-sm"
              style={{
                borderColor: plan.popular ? plan.color : brand.line,
                boxShadow: plan.popular ? `0 24px 60px ${plan.color}1F` : undefined,
              }}
            >
              {plan.popular && (
                <div className="absolute inset-x-0 top-0 h-1.5" style={{ background: `linear-gradient(90deg, ${brand.primary}, ${brand.accentGold})` }} />
              )}
              <span className="inline-flex rounded-full px-3 py-1 text-[11px] font-black text-white" style={{ background: plan.color }}>
                {plan.tag}
              </span>
              <div className="mt-4 text-sm font-black" style={{ color: brand.ink }}>{plan.label}</div>
              <div className="mt-3 text-4xl font-black" style={{ color: plan.color }}>{plan.price}</div>
              <div className="mt-1 text-xs font-semibold" style={{ color: brand.muted }}>{plan.sub}</div>
              <ul className="mt-5 space-y-2 text-left">
                {plan.perks.map(perk => (
                  <li key={perk} className="flex items-start gap-2 text-xs leading-relaxed" style={{ color: brand.muted }}>
                    <CheckCircle2 className="mt-0.5 size-3.5 shrink-0" style={{ color: plan.color }} />
                    {perk}
                  </li>
                ))}
              </ul>
              <Link
                to="/register"
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl py-3 text-sm font-black text-white"
                style={{ background: plan.popular ? brand.primary : brand.plum }}
              >
                Crear cuenta
                <ArrowRight className="size-4" />
              </Link>
            </motion.div>
          ))}
        </div>

        <motion.div
          {...fadeUp}
          className="mx-auto mt-8 overflow-hidden rounded-2xl border bg-white shadow-sm"
          style={{ borderColor: brand.line }}
        >
          <div className="border-b p-5 text-center" style={{ borderColor: brand.line, background: brand.bg }}>
            <p className="text-xs font-black uppercase tracking-[0.18em]" style={{ color: brand.primary }}>
              Comparación rápida
            </p>
            <h3 className="mt-2 text-xl font-black" style={{ color: brand.ink }}>
              Elige por impacto operativo, no solo por precio.
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead>
                <tr className="border-b" style={{ borderColor: brand.line }}>
                  <th className="px-4 py-3 text-xs font-black uppercase tracking-[0.12em]" style={{ color: brand.muted }}>Beneficio</th>
                  {DELIVERY_SERVICES.map(service => (
                    <th key={service.label} className="px-4 py-3 text-xs font-black uppercase tracking-[0.12em]" style={{ color: service.color }}>
                      {service.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {COMPARISON_ROWS.map(([label, classic, coordinated, premium]) => (
                  <tr key={label} className="border-b last:border-b-0" style={{ borderColor: brand.line }}>
                    <td className="px-4 py-3 font-bold" style={{ color: brand.ink }}>{label}</td>
                    <td className="px-4 py-3" style={{ color: brand.muted }}>{classic}</td>
                    <td className="px-4 py-3 font-semibold" style={{ color: brand.ink }}>{coordinated}</td>
                    <td className="px-4 py-3" style={{ color: brand.muted }}>{premium}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="border-t px-5 py-4 text-center text-xs leading-relaxed" style={{ borderColor: brand.line, color: brand.muted }}>
            Coordinado y Premium están diseñados para mejorar la probabilidad de entrega efectiva, pero no garantizan
            aceptación, pago ni resultado comercial específico.
          </p>
        </motion.div>

        <motion.div {...fadeUp} className="mx-auto mb-6 mt-12 max-w-2xl text-center">
          <p className="mb-3 text-xs font-black uppercase tracking-[0.2em]" style={{ color: brand.primary }}>
            Add-ons opcionales
          </p>
          <h3 className="text-2xl font-black md:text-3xl" style={{ color: brand.ink }}>
            Agrega protección operativa cuando tu pedido lo necesita.
          </h3>
          <p className="mt-3 text-sm leading-relaxed" style={{ color: brand.muted }}>
            Los add-ons permiten ajustar la operación sin complicar la tarifa base. Más opciones se irán sumando a la plataforma.
          </p>
        </motion.div>

        <div className="mx-auto grid max-w-5xl gap-4 md:grid-cols-3">
          {ADDONS.map((addon, index) => (
            <motion.div
              key={addon.label}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: index * 0.05 }}
              className="rounded-2xl border bg-white p-5 shadow-sm"
              style={{ borderColor: brand.line }}
            >
              <div className="text-xs font-black uppercase tracking-[0.14em]" style={{ color: addon.color }}>{addon.label}</div>
              <div className="mt-3 text-3xl font-black" style={{ color: addon.color }}>{addon.price}</div>
              <p className="mt-2 text-xs leading-relaxed" style={{ color: brand.muted }}>{addon.sub}</p>
            </motion.div>
          ))}
        </div>

        <motion.div {...fadeUp} className="mx-auto mb-8 mt-14 max-w-2xl text-center">
          <p className="mb-3 text-xs font-black uppercase tracking-[0.2em]" style={{ color: brand.primary }}>
            Planes para tiendas
          </p>
          <h3 className="text-2xl font-black md:text-3xl" style={{ color: brand.ink }}>
            Más visibilidad, prioridad y control operativo para vender mejor.
          </h3>
        </motion.div>

        <div className="mx-auto grid max-w-5xl gap-4 md:grid-cols-2">
          {SUBSCRIPTION_PLANS.map((plan, index) => (
            <motion.div
              key={plan.label}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: index * 0.06 }}
              className="relative overflow-hidden rounded-2xl border bg-white p-6 shadow-sm"
              style={{
                borderColor: plan.popular ? brand.primary : brand.line,
                boxShadow: plan.popular ? `0 24px 60px ${brand.primary}1F` : undefined,
              }}
            >
              {plan.popular && (
                <div className="absolute inset-x-0 top-0 h-1.5" style={{ background: `linear-gradient(90deg, ${brand.primary}, ${brand.plum})` }} />
              )}
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-black" style={{ color: brand.ink }}>{plan.label}</div>
                  <p className="mt-2 text-sm leading-relaxed" style={{ color: brand.muted }}>{plan.sub}</p>
                </div>
                {plan.popular && (
                  <span className="rounded-full px-3 py-1 text-[11px] font-black text-white" style={{ background: brand.primary }}>
                    Recomendado
                  </span>
                )}
              </div>

              <div className="mt-5 flex items-end gap-1.5">
                <span className="text-4xl font-black" style={{ color: brand.primary }}>{plan.price}</span>
                <span className="pb-1 text-sm font-bold" style={{ color: brand.muted }}>{plan.period}</span>
              </div>

              <ul className="mt-6 space-y-3">
                {plan.perks.map(perk => (
                  <li key={perk} className="flex items-start gap-2 text-sm" style={{ color: brand.muted }}>
                    <CheckCircle2 className="mt-0.5 size-4 shrink-0" style={{ color: brand.accentGreen }} />
                    {perk}
                  </li>
                ))}
              </ul>

              <Link
                to="/register"
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl py-3 text-sm font-black text-white"
                style={{ background: plan.popular ? brand.primary : brand.plum }}
              >
                Elegir plan
                <ArrowRight className="size-4" />
              </Link>
            </motion.div>
          ))}
        </div>

        <p className="mt-6 text-center text-xs" style={{ color: brand.muted }}>
          Tarifas base sujetas a cobertura, capacidad operativa, volumen, tipo de producto y confirmación de condiciones.
          Recojos en tienda hasta 12:00 PM; entregas same-day hasta 2:00 PM o hasta llegada del driver si corresponden a un recojo activo.
        </p>
      </div>
    </section>
  );
}
